/**
 * F3 — ConflictDialogHost. Listens for the conflict events dispatched by
 * `useAutoSaveBack` (via `repancy:save-back-conflict`) and the queue drain
 * worker (via `repancy:queue-conflict`), and renders the existing
 * `ConflictDialog` component with the right title + handlers.
 *
 * Mount once near the app root. Doesn't itself call Graph — uses
 * `resolveConflict` from saveBack.ts.
 */

import { useEffect, useState } from 'react';
import type { SharePointExternalRef } from '@repancy/core';
import { ConflictDialog } from './ConflictDialog';
import {
  resolveConflict,
  type ConflictResolution,
  type SaveBackResult,
} from '../../lib/graph/saveBack';
import { pushGraphErrorToast } from '../../lib/graph/useGraphErrorToast';

interface ConflictEventDetail {
  externalRef: SharePointExternalRef;
  localContent: string;
  remoteEtag: string;
  documentTitle?: string;
  remoteAuthor?: string;
  /** Queue row id, when the conflict came from the drain worker. */
  queueRowId?: string;
}

export function ConflictDialogHost() {
  const [pending, setPending] = useState<ConflictEventDetail | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<ConflictEventDetail>).detail;
      if (!detail) return;
      setPending(detail);
    };
    const queueHandler = (ev: Event) => {
      // The drain worker dispatches { row, errorKind, errorMessage }. Adapt
      // it to the ConflictDialog shape.
      const detail = (ev as CustomEvent<{ row: { id: string; targetRef: { driveId: string; itemId: string; siteId?: string }; payload: unknown; baseEtag?: string }; errorMessage: string }>).detail;
      if (!detail) return;
      const payload = detail.row.payload as { body?: string } | undefined;
      setPending({
        externalRef: {
          siteId: detail.row.targetRef.siteId ?? '',
          driveId: detail.row.targetRef.driveId,
          itemId: detail.row.targetRef.itemId,
          etag: detail.row.baseEtag ?? '',
          webUrl: '',
        },
        localContent: payload?.body ?? '',
        remoteEtag: '',
        documentTitle: `Queued edit (${detail.row.targetRef.itemId.slice(-8)})`,
        queueRowId: detail.row.id,
      });
    };
    window.addEventListener('repancy:save-back-conflict', handler);
    window.addEventListener('repancy:queue-conflict', queueHandler);
    return () => {
      window.removeEventListener('repancy:save-back-conflict', handler);
      window.removeEventListener('repancy:queue-conflict', queueHandler);
    };
  }, []);

  async function onResolve(choice: ConflictResolution) {
    if (!pending) return;
    let result: SaveBackResult;
    try {
      result = await resolveConflict(
        pending.externalRef,
        choice,
        pending.localContent,
        pending.remoteEtag,
      );
    } catch (err) {
      pushGraphErrorToast(err);
      return;
    }
    if (result.kind === 'error') {
      pushGraphErrorToast(new Error(`Conflict resolution failed: ${result.message}`));
      return;
    }
    // Notify any subscribers (e.g. useAutoSaveBack hook holding stale state).
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent('repancy:save-back-conflict-resolved', {
            detail: {
              externalRef: pending.externalRef,
              choice,
              result,
              queueRowId: pending.queueRowId,
            },
          }),
        );
      } catch {
        // ignore
      }
    }
    setPending(null);
  }

  return (
    <ConflictDialog
      open={pending !== null}
      documentTitle={pending?.documentTitle ?? 'document'}
      remoteAuthor={pending?.remoteAuthor}
      onResolve={onResolve}
      onCancel={() => setPending(null)}
    />
  );
}
